import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';

// ── Error Boundary ────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('Staff page error:', error, info); }
  render() {
    if (this.state.hasError) return (
      <div style={{ padding: 24, background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: 12, color: '#FF6B6B' }}>
        <h3 style={{ margin: '0 0 8px' }}>Error in Staff Management</h3>
        <p style={{ fontSize: 13, margin: 0 }}>{this.state.error?.message}</p>
        <button onClick={() => this.setState({ hasError: false, error: null })} style={{ marginTop: 12, padding: '8px 16px', background: '#F0B429', color: '#0B0E17', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
          Try Again
        </button>
      </div>
    );
    return this.props.children;
  }
}

// ── Constants ─────────────────────────────────────────────────────────────────
const API = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const GHS = (n) => `GHS ${Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tok = () => localStorage.getItem('token');
const authFetch = (url, opts = {}) =>
  fetch(url, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}`, ...(opts.headers || {}) } });

const TABS = ['Overview', 'Attendance', 'Shifts', 'Payroll', 'Commissions', 'Advances', 'Audit'];
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg,#F0B429,#FFCD3C)',
  'linear-gradient(135deg,#00C9B1,#00E5CC)',
  'linear-gradient(135deg,#A78BFA,#C4B5FD)',
  'linear-gradient(135deg,#38BDF8,#7DD3FC)',
  'linear-gradient(135deg,#FB7185,#FDA4AF)',
  'linear-gradient(135deg,#FF6B6B,#FF9090)',
];
const avatarGrad = (id) => AVATAR_GRADIENTS[(id || 0) % AVATAR_GRADIENTS.length];
const initials = (s) => `${s.firstName?.[0] || ''}${s.lastName?.[0] || ''}`.toUpperCase();

const SHIFT_STYLES = {
  morning:   { bg: 'rgba(0,201,177,0.15)',   color: '#00C9B1',  label: 'AM' },
  afternoon: { bg: 'rgba(240,180,41,0.15)',  color: '#F0B429',  label: 'PM' },
  evening:   { bg: 'rgba(167,139,250,0.15)', color: '#A78BFA',  label: 'Full' },
  off:       { bg: 'rgba(255,255,255,0.04)', color: '#3D4559',  label: 'Off' },
  holiday:   { bg: 'rgba(240,180,41,0.08)',  color: '#F0B429',  label: 'Leave' },
};

function getWeekDates(offset = 0) {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

// ── Styles ────────────────────────────────────────────────────────────────────
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Cabinet+Grotesk:wght@400;500;700;800;900&family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@400;600&display=swap');
@keyframes sm-blink{0%,100%{opacity:1}50%{opacity:0.3}}
@keyframes sm-pop{from{opacity:0;transform:scale(0.94) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}
.sm-wrap{font-family:'Cabinet Grotesk',sans-serif;padding:28px 32px}
.sm-card{background:#1E2235;border:1px solid rgba(255,255,255,0.06);border-radius:18px;padding:22px;transition:border-color 0.2s}
.sm-card:hover{border-color:rgba(255,255,255,0.1)}
.sm-sw{font-size:11px;font-weight:700;padding:5px 10px;border-radius:6px;cursor:pointer;color:#6B7A99;transition:all 0.15s;border:none;background:none;font-family:'Cabinet Grotesk',sans-serif}
.sm-sw.on{background:#1E2235;color:#FFFFFF;box-shadow:0 1px 4px rgba(0,0,0,0.3)}
.sm-sw:hover:not(.on){color:#FFFFFF}
.sm-ptab{font-size:13px;font-weight:700;padding:10px 18px;cursor:pointer;color:#6B7A99;transition:all 0.15s;border-bottom:3px solid transparent;margin-bottom:-1px}
.sm-ptab:hover{color:#FFFFFF}
.sm-ptab.on{color:#F0B429;border-bottom-color:#F0B429}
.sm-kpi{background:#1E2235;border:1px solid rgba(255,255,255,0.06);border-radius:14px;padding:16px;position:relative;overflow:hidden;transition:transform 0.2s,border-color 0.2s}
.sm-kpi:hover{transform:translateY(-2px);border-color:rgba(255,255,255,0.1)}
.sm-kpi::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;border-radius:14px 14px 0 0}
.sm-kpi.gold::before{background:linear-gradient(90deg,#F0B429,#FFCD3C)}
.sm-kpi.teal::before{background:linear-gradient(90deg,#00C9B1,#00E5CC)}
.sm-kpi.coral::before{background:linear-gradient(90deg,#FF6B6B,#FF9090)}
.sm-kpi.lav::before{background:linear-gradient(90deg,#A78BFA,#C4B5FD)}
.sm-kpi.sky::before{background:linear-gradient(90deg,#38BDF8,#7DD3FC)}
.sm-staff-card{background:#252A3D;border:1px solid rgba(255,255,255,0.06);border-radius:16px;padding:18px;cursor:pointer;transition:all 0.2s;position:relative;overflow:hidden}
.sm-staff-card:hover{transform:translateY(-3px);border-color:rgba(255,255,255,0.12);box-shadow:0 8px 32px rgba(0,0,0,0.3)}
.sm-staff-card.clocked-in{border-color:rgba(0,201,177,0.3)}
.sm-table-row{display:grid;padding:12px;border-radius:10px;cursor:pointer;transition:background 0.12s;align-items:center;border-bottom:1px solid rgba(255,255,255,0.03)}
.sm-table-row:hover{background:rgba(255,255,255,0.04)}
.sm-table-row:last-child{border-bottom:none}
.sm-btn-ghost{display:flex;align-items:center;gap:7px;padding:0 16px;height:38px;border-radius:10px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.06);color:#6B7A99;font-size:13px;font-weight:700;cursor:pointer;transition:all 0.15s;font-family:'Cabinet Grotesk',sans-serif}
.sm-btn-ghost:hover{background:rgba(255,255,255,0.09);color:#FFFFFF}
.sm-btn-gold{display:flex;align-items:center;gap:7px;padding:0 18px;height:38px;border-radius:10px;background:#F0B429;color:#0B0E17;font-size:13px;font-weight:800;cursor:pointer;border:none;font-family:'Cabinet Grotesk',sans-serif;transition:all 0.15s}
.sm-btn-gold:hover{background:#FFCD3C;transform:translateY(-1px);box-shadow:0 8px 24px rgba(240,180,41,0.3)}
.sm-btn-sm{padding:5px 12px;border-radius:8px;font-size:11px;font-weight:700;cursor:pointer;font-family:'Cabinet Grotesk',sans-serif;border:none;transition:all 0.15s}
.sm-chip{font-size:11px;font-weight:700;padding:4px 10px;border-radius:100px}
.sm-chip.gold{background:rgba(240,180,41,0.12);color:#F0B429}
.sm-chip.teal{background:rgba(0,201,177,0.12);color:#00C9B1}
.sm-chip.coral{background:rgba(255,107,107,0.12);color:#FF6B6B}
.sm-chip.lav{background:rgba(167,139,250,0.12);color:#A78BFA}
.sm-chip.sky{background:rgba(56,189,248,0.12);color:#38BDF8}
.sm-input{width:100%;padding:10px 14px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);border-radius:10px;color:#FFFFFF;font-size:13px;font-family:'Cabinet Grotesk',sans-serif;outline:none;transition:border-color 0.15s}
.sm-input:focus{border-color:#F0B429}
.sm-input option{background:#1E2235}
.sm-label{font-size:11px;font-weight:700;color:#6B7A99;margin-bottom:6px;display:block;text-transform:uppercase;letter-spacing:0.5px}
.sm-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.72);backdrop-filter:blur(4px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:20px}
.sm-modal{background:#1E2235;border:1px solid rgba(255,255,255,0.1);border-radius:20px;padding:28px;width:100%;max-height:90vh;overflow-y:auto;animation:sm-pop 280ms cubic-bezier(0.34,1.56,0.64,1) forwards}
.sm-comm-row{display:flex;align-items:center;gap:12px;padding:10px;border-radius:10px;cursor:pointer;transition:background 0.12s;margin-bottom:2px}
.sm-comm-row:hover{background:rgba(255,255,255,0.04)}
.sm-adv-item{display:flex;align-items:center;gap:12px;padding:12px;border-radius:12px;border:1px solid rgba(255,255,255,0.06);background:rgba(255,255,255,0.02);margin-bottom:8px;cursor:pointer;transition:border-color 0.15s}
.sm-adv-item:hover{border-color:rgba(255,255,255,0.1)}
.sm-shift-select{font-size:11px;font-weight:700;border:none;border-radius:6px;padding:4px 6px;cursor:pointer;width:100%;font-family:'Cabinet Grotesk',sans-serif;transition:all 0.15s}
.sm-code-input{width:100%;padding:14px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);border-radius:12px;color:#FFFFFF;font-size:22px;font-family:'JetBrains Mono',monospace;font-weight:700;letter-spacing:10px;text-align:center;outline:none;transition:border-color 0.15s;text-transform:uppercase}
.sm-code-input:focus{border-color:#F0B429}
.sm-selfie-btn{width:100%;padding:18px;border-radius:12px;background:rgba(56,189,248,0.06);border:1.5px dashed rgba(56,189,248,0.3);color:#38BDF8;font-size:13px;font-weight:700;cursor:pointer;font-family:'Cabinet Grotesk',sans-serif;transition:all 0.15s;display:flex;align-items:center;justify-content:center;gap:8px}
.sm-selfie-btn:hover{background:rgba(56,189,248,0.12)}
.sm-selfie-btn.captured{background:rgba(0,201,177,0.08);border-color:rgba(0,201,177,0.3);color:#00C9B1}
.sm-flag-row{display:flex;align-items:flex-start;gap:12px;padding:14px;border-radius:12px;border:1px solid rgba(255,107,107,0.2);background:rgba(255,107,107,0.05);margin-bottom:8px}
.sm-audit-log{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:10px;border-bottom:1px solid rgba(255,255,255,0.03);font-size:12px}
.sm-audit-log:last-child{border-bottom:none}
.sm-daily-code-box{background:rgba(240,180,41,0.06);border:1px solid rgba(240,180,41,0.2);border-radius:14px;padding:16px 20px;display:flex;align-items:center;gap:16px}
`;

// ── Helpers ───────────────────────────────────────────────────────────────────
function Sk({ h = 20, w = '100%', r = 6 }) {
  return <div style={{ height: h, width: w, borderRadius: r, background: 'rgba(255,255,255,0.06)', flexShrink: 0 }} />;
}

function Avatar({ s, size = 36 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: avatarGrad(s.id), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.33, fontWeight: 900, color: '#0B0E17', flexShrink: 0 }}>
      {initials(s)}
    </div>
  );
}

function ModalClose({ onClose }) {
  return (
    <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#6B7A99', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
  );
}

// ── Clock-In Modal ────────────────────────────────────────────────────────────
function ClockInModal({ staff, onClose, onSuccess }) {
  const [staffId, setStaffId]               = useState('');
  const [dailyCode, setDailyCode]           = useState('');
  const [selfieUrl, setSelfieUrl]           = useState('');
  const [selfieCaptured, setSelfieCaptured] = useState(false);
  const [uploading, setUploading]           = useState(false);
  const [location, setLocation]             = useState(null);
  const [gpsError, setGpsError]             = useState('');
  const [gpsLoading, setGpsLoading]         = useState(false);
  const [error, setError]                   = useState('');
  const [submitting, setSubmitting]         = useState(false);
  const [done, setDone]                     = useState(false);
  const [clockOut, setClockOut]             = useState(false);
  const [cfg, setCfg]                       = useState({ geofenceEnabled: false, dailyCodeEnabled: false, selfieRequired: false });

  useEffect(() => {
    authFetch(`${API}/staff/attendance/settings`)
      .then(r => r.ok ? r.json() : {})
      .then(s => setCfg({ geofenceEnabled: !!s.geofenceEnabled, dailyCodeEnabled: !!s.dailyCodeEnabled, selfieRequired: !!s.selfieRequired }))
      .catch(() => {});
  }, []);

  const requestGPS = () => {
    setGpsError('');
    setGpsLoading(true);
    if (!navigator.geolocation) {
      setGpsError('GPS not available on this device');
      setGpsLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGpsLoading(false);
      },
      (err) => {
        setGpsError(err.code === 1 ? 'Please allow location access to clock in' : 'Could not get your location. Try again.');
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const captureSelfie = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'user';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('image', file);
        const r = await fetch(`${API}/uploads/attendance-selfie`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${tok()}` },
          body: fd,
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Upload failed');
        setSelfieUrl(d.url);
        setSelfieCaptured(true);
      } catch (err) {
        console.error('[StaffManagement] selfie upload error:', err);
        setError('Selfie upload failed. Please try again.');
      }
      setUploading(false);
    };
    input.click();
  };

  const submit = async () => {
    if (!staffId) { setError('Select a staff member'); return; }
    setError('');
    setSubmitting(true);
    const endpoint = clockOut ? 'clock-out' : 'clock-in';
    const body = clockOut
      ? { staffId, latitude: location?.latitude, longitude: location?.longitude }
      : { staffId, dailyCode, selfieUrl: selfieUrl || null, latitude: location?.latitude, longitude: location?.longitude, accuracy: location?.accuracy };
    try {
      const r = await authFetch(`${API}/staff/attendance/${endpoint}`, { method: 'POST', body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Failed'); setSubmitting(false); return; }
      setDone(true);
      setTimeout(() => { onSuccess?.(); onClose(); }, 1400);
    } catch (err) {
      console.error('[StaffManagement] attendance submit error:', err);
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  if (done) return (
    <div className="sm-overlay">
      <div className="sm-modal" style={{ maxWidth: 340, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#00C9B1' }}>{clockOut ? 'Clocked Out' : 'Clocked In'} Successfully</div>
        <div style={{ fontSize: 12, color: '#6B7A99', marginTop: 6 }}>{new Date().toLocaleTimeString('en-GH')}</div>
      </div>
    </div>
  );

  return (
    <div className="sm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sm-modal" style={{ maxWidth: 420 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#FFFFFF' }}>
            {clockOut ? 'Clock Out' : 'Clock In'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={`sm-btn-ghost${!clockOut ? ' on' : ''}`} style={!clockOut ? { background: 'rgba(0,201,177,0.12)', color: '#00C9B1', border: '1px solid rgba(0,201,177,0.25)' } : {}} onClick={() => setClockOut(false)}>Clock In</button>
            <button className={`sm-btn-ghost${clockOut ? ' on' : ''}`} style={clockOut ? { background: 'rgba(255,107,107,0.12)', color: '#FF6B6B', border: '1px solid rgba(255,107,107,0.25)' } : {}} onClick={() => setClockOut(true)}>Clock Out</button>
            <ModalClose onClose={onClose} />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Staff picker */}
          <div>
            <label className="sm-label">Staff Member</label>
            <select className="sm-input" value={staffId} onChange={e => setStaffId(e.target.value)}>
              <option value="">Select…</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
            </select>
          </div>

          {!clockOut && cfg.dailyCodeEnabled && (
            <div>
              <label className="sm-label">Daily Code</label>
              <input
                className="sm-code-input"
                maxLength={6}
                placeholder="• • • • • •"
                value={dailyCode}
                onChange={e => setDailyCode(e.target.value.toUpperCase())}
              />
              <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 5 }}>Get today's 6-digit code from your manager</div>
            </div>
          )}

          {/* GPS — shown when geofence is enabled OR always show on clock-out for audit trail */}
          {(cfg.geofenceEnabled || clockOut) && (
          <div>
            <label className="sm-label">Location {location && '✓'}</label>
            {location ? (
              <div style={{ padding: '10px 14px', background: 'rgba(0,201,177,0.08)', border: '1px solid rgba(0,201,177,0.2)', borderRadius: 10, fontSize: 12, color: '#00C9B1', fontWeight: 700 }}>
                GPS captured · ±{Math.round(location.accuracy)}m accuracy
                <button onClick={() => setLocation(null)} style={{ marginLeft: 10, fontSize: 10, color: '#6B7A99', background: 'none', border: 'none', cursor: 'pointer' }}>reset</button>
              </div>
            ) : (
              <button className="sm-btn-ghost" style={{ width: '100%', justifyContent: 'center', height: 44 }} onClick={requestGPS} disabled={gpsLoading}>
                {gpsLoading ? 'Getting location…' : '📍 Capture Location'}
              </button>
            )}
            {gpsError && <div style={{ fontSize: 11, color: '#FF6B6B', marginTop: 5 }}>{gpsError}</div>}
          </div>
          )}

          {!clockOut && cfg.selfieRequired && (
            <div>
              <label className="sm-label">Selfie {selfieCaptured && '✓'}</label>
              <button className={`sm-selfie-btn${selfieCaptured ? ' captured' : ''}`} onClick={captureSelfie} disabled={uploading}>
                {uploading ? 'Uploading…' : selfieCaptured ? '✓ Selfie Captured' : '📷 Take Selfie'}
              </button>
            </div>
          )}

          {error && (
            <div style={{ padding: '12px 14px', background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.25)', borderRadius: 10, fontSize: 13, color: '#FF6B6B', fontWeight: 600 }}>
              {error}
            </div>
          )}
        </div>

        <button
          className="sm-btn-gold"
          style={{ width: '100%', justifyContent: 'center', marginTop: 24, height: 46, fontSize: 15 }}
          onClick={submit}
          disabled={submitting || (!clockOut && cfg.selfieRequired && !selfieCaptured)}
        >
          {submitting ? 'Submitting…' : clockOut ? 'Clock Out' : (cfg.selfieRequired && !selfieCaptured) ? 'Take Selfie First' : 'Clock In'}
        </button>
      </div>
    </div>
  );
}

// ── Audit Tab ─────────────────────────────────────────────────────────────────
const SETTINGS_EMPTY = {
  geofenceEnabled: false, shopLatitude: '', shopLongitude: '',
  shopRadiusMetres: 100, dailyCodeEnabled: false, selfieRequired: false,
};

function Toggle({ on, onChange, color = '#00C9B1' }) {
  return (
    <div
      onClick={() => onChange(!on)}
      style={{ width: 40, height: 22, borderRadius: 100, background: on ? color : 'rgba(255,255,255,0.1)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
    >
      <div style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#FFFFFF', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
    </div>
  );
}

function AuditTab() {
  const [date, setDate]               = useState(new Date().toISOString().split('T')[0]);
  const [flagged, setFlagged]         = useState([]);
  const [logs, setLogs]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [dailyCode, setDailyCode]     = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [settings, setSettings]       = useState(SETTINGS_EMPTY);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [saving, setSaving]           = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [fR, lR, sR] = await Promise.all([
      authFetch(`${API}/staff/attendance/flagged`),
      authFetch(`${API}/staff/attendance/logs?date=${date}`),
      authFetch(`${API}/staff/attendance/settings`),
    ]);
    if (fR.ok) setFlagged(await fR.json());
    if (lR.ok) setLogs(await lR.json());
    if (sR.ok) {
      const s = await sR.json();
      setSettings({
        geofenceEnabled:  s.geofenceEnabled  ?? false,
        shopLatitude:     s.shopLatitude     ?? '',
        shopLongitude:    s.shopLongitude    ?? '',
        shopRadiusMetres: s.shopRadiusMetres ?? 100,
        dailyCodeEnabled: s.dailyCodeEnabled ?? false,
        selfieRequired:   s.selfieRequired   ?? false,
      });
    }
    setLoading(false);
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    const r = await authFetch(`${API}/staff/attendance/settings`, {
      method: 'PUT', body: JSON.stringify(settings),
    });
    if (r.ok) { setSettingsSaved(true); setTimeout(() => setSettingsSaved(false), 2000); }
    setSaving(false);
  };

  const sf = (k) => (v) => setSettings(s => ({ ...s, [k]: v }));

  const fetchCode = async () => {
    setCodeLoading(true);
    const r = await authFetch(`${API}/staff/daily-code`);
    if (r.ok) { const d = await r.json(); setDailyCode(d.code); }
    else setDailyCode('No access');
    setCodeLoading(false);
  };

  const todayFlagged = flagged.filter(f => f.date?.startsWith(date));

  return (
    <div>
      {/* ── Anti-fraud settings ── */}
      <div className="sm-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#FFFFFF' }}>Anti-Fraud Settings</div>
            <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>Per-workspace — each business configures their own shop location</div>
          </div>
          <button
            className="sm-btn-gold"
            onClick={saveSettings}
            disabled={saving}
            style={{ minWidth: 90 }}
          >
            {saving ? 'Saving…' : settingsSaved ? '✓ Saved' : 'Save'}
          </button>
        </div>

        {/* Toggles */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { key: 'geofenceEnabled',  label: 'GPS Geofencing',     sub: 'Block clock-ins from outside your shop radius', color: '#00C9B1' },
            { key: 'dailyCodeEnabled', label: 'Daily Rotating Code', sub: 'Staff must enter a 6-digit code that changes at midnight', color: '#F0B429' },
            { key: 'selfieRequired',   label: 'Selfie on Clock-in',  sub: 'Require a front-camera photo before clocking in', color: '#A78BFA' },
          ].map(({ key, label, sub, color }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <Toggle on={settings[key]} onChange={sf(key)} color={color} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: settings[key] ? '#FFFFFF' : '#6B7A99' }}>{label}</div>
                <div style={{ fontSize: 11, color: '#3D4559', marginTop: 1 }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* GPS coordinates — only shown when geofence is on */}
        {settings.geofenceEnabled && (
          <div style={{ marginTop: 18, padding: '16px', background: 'rgba(0,201,177,0.05)', border: '1px solid rgba(0,201,177,0.15)', borderRadius: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#00C9B1', marginBottom: 12 }}>Shop GPS Coordinates</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div>
                <label className="sm-label">Latitude</label>
                <input
                  className="sm-input"
                  placeholder="e.g. 5.6037"
                  value={settings.shopLatitude}
                  onChange={e => sf('shopLatitude')(e.target.value)}
                />
              </div>
              <div>
                <label className="sm-label">Longitude</label>
                <input
                  className="sm-input"
                  placeholder="e.g. -0.1870"
                  value={settings.shopLongitude}
                  onChange={e => sf('shopLongitude')(e.target.value)}
                />
              </div>
              <div>
                <label className="sm-label">Radius (metres)</label>
                <input
                  className="sm-input"
                  type="number"
                  min="10"
                  max="5000"
                  value={settings.shopRadiusMetres}
                  onChange={e => sf('shopRadiusMetres')(Number(e.target.value))}
                />
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 10 }}>
              Open Google Maps → long-press your shop → copy the coordinates shown at the bottom
            </div>
          </div>
        )}
      </div>

      {/* Daily code box */}
      <div className="sm-daily-code-box" style={{ marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#F0B429', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 4 }}>Today's Staff Code</div>
          <div style={{ fontSize: 28, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', letterSpacing: 6, color: dailyCode ? '#F0B429' : '#3D4559' }}>
            {dailyCode || '• • • • • •'}
          </div>
          <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 4 }}>Expires at midnight · Show this to staff or print and post at entry</div>
        </div>
        <button className="sm-btn-gold" onClick={fetchCode} disabled={codeLoading}>{codeLoading ? 'Loading…' : 'Reveal Code'}</button>
      </div>

      {/* Date picker + stats */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <input type="date" className="sm-input" value={date} onChange={e => setDate(e.target.value)} style={{ width: 'auto' }} />
        <div style={{ fontSize: 12, color: '#6B7A99', fontWeight: 600 }}>
          {logs.length} attempts · {logs.filter(l => !l.approved).length} rejected · {todayFlagged.length} flagged
        </div>
      </div>

      {/* Flagged records for this date */}
      {todayFlagged.length > 0 && (
        <div className="sm-card" style={{ marginBottom: 16, borderColor: 'rgba(255,107,107,0.2)' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#FF6B6B', marginBottom: 14 }}>🚩 Flagged Records</div>
          {todayFlagged.map(f => (
            <div key={f.id} className="sm-flag-row">
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: avatarGrad(f.staffId), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, color: '#0B0E17', flexShrink: 0 }}>
                {f.staff ? initials(f.staff) : '?'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', marginBottom: 3 }}>
                  {f.staff?.firstName} {f.staff?.lastName}
                  {f.distanceFromShop != null && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#6B7A99', marginLeft: 8 }}>Final: {f.distanceFromShop}m from shop</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#FF6B6B', fontWeight: 600 }}>{f.flagReason}</div>
                <div style={{ fontSize: 10, color: '#6B7A99', marginTop: 3 }}>
                  Clock in: {f.clockIn ? new Date(f.clockIn).toLocaleTimeString('en-GH') : '—'}
                  {f.clockInAccuracy && ` · GPS ±${Math.round(f.clockInAccuracy)}m`}
                </div>
                {f.clockInSelfie && (
                  <a href={f.clockInSelfie} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#38BDF8', fontWeight: 700, textDecoration: 'none', marginTop: 4, display: 'inline-block' }}>View Selfie →</a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Raw attempt log */}
      <div className="sm-card">
        <div style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF', marginBottom: 14 }}>Attempt Log — {date}</div>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Array(5).fill(0).map((_, i) => <Sk key={i} h={40} r={8} />)}
          </div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#6B7A99', fontSize: 13 }}>No clock-in attempts logged for this date</div>
        ) : logs.map((l) => (
          <div key={l.id} className="sm-audit-log" style={{ color: l.approved ? '#FFFFFF' : '#FF6B6B' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: l.approved ? '#00C9B1' : '#FF6B6B', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 700 }}>{l.type === 'CLOCK_IN_ATTEMPT' ? 'Clock-in attempt' : l.type}</span>
              {l.distanceFromShop != null && (
                <span style={{ color: '#6B7A99', marginLeft: 8 }}>{l.distanceFromShop}m from shop</span>
              )}
              {l.accuracy && (
                <span style={{ color: '#6B7A99', marginLeft: 8 }}>±{Math.round(l.accuracy)}m GPS</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#6B7A99', fontFamily: '"JetBrains Mono",monospace', flexShrink: 0 }}>
              {new Date(l.timestamp).toLocaleTimeString('en-GH')}
            </div>
            <div style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 100, background: l.approved ? 'rgba(0,201,177,0.12)' : 'rgba(255,107,107,0.12)', color: l.approved ? '#00C9B1' : '#FF6B6B', flexShrink: 0 }}>
              {l.approved ? 'OK' : 'REJECTED'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Directory Tab ─────────────────────────────────────────────────────────────
const STAFF_FORM_EMPTY = {
  firstName: '', lastName: '', phone: '', email: '', role: 'staff',
  basicSalary: '', commissionRate: '', transportAllow: '', mealAllow: '',
  ssnitNumber: '', ghanaCardNumber: '',
  bankName: '', bankAccount: '', bankCode: '',
  momoNetwork: 'MTN', momoNumber: '',
  notes: '',
};

function DirectoryTab({ forceOpen, onForceOpenConsumed }) {
  const [staff, setStaff] = useState([]);
  const [todayAtt, setTodayAtt] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(STAFF_FORM_EMPTY);
  const today = new Date().toISOString().split('T')[0];

  const load = useCallback(async () => {
    setLoading(true);
    const [sR, aR] = await Promise.all([
      authFetch(`${API}/staff`),
      authFetch(`${API}/staff/attendance?date=${today}`),
    ]);
    if (sR.ok) setStaff(await sR.json());
    if (aR.ok) {
      const d = await aR.json();
      setTodayAtt(Array.isArray(d) ? d : (d.records || []));
    }
    setLoading(false);
  }, [today]);

  useEffect(() => { load(); }, [load]);

  const getStatus = (id) => {
    const rec = todayAtt.find(a => a.staffId === id || a.staff?.id === id);
    if (!rec) return 'out';
    if (rec.clockIn && !rec.clockOut) return 'in';
    if (rec.status === 'absent') return 'absent';
    return 'done';
  };

  const openNew = () => { setEditing(null); setForm(STAFF_FORM_EMPTY); setShowForm(true); };

  useEffect(() => {
    if (forceOpen) { openNew(); onForceOpenConsumed?.(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen]);

  const openEdit = (s) => {
    setEditing(s);
    setForm({ firstName: s.firstName, lastName: s.lastName, phone: s.phone || '', email: s.email || '', role: s.role, basicSalary: s.basicSalary, commissionRate: Number(s.commissionRate) * 100, transportAllow: s.transportAllow, mealAllow: s.mealAllow, ssnitNumber: s.ssnitNumber || '', ghanaCardNumber: s.ghanaCardNumber || '', bankName: s.bankName || '', bankAccount: s.bankAccount || '', bankCode: s.bankCode || '', momoNetwork: s.momoNetwork || 'MTN', momoNumber: s.momoNumber || '', notes: s.notes || '' });
    setShowForm(true);
  };

  const save = async () => {
    const payload = { ...form, commissionRate: Number(form.commissionRate) / 100 };
    const r = editing
      ? await authFetch(`${API}/staff/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) })
      : await authFetch(`${API}/staff`, { method: 'POST', body: JSON.stringify(payload) });
    if (r.ok) { setShowForm(false); load(); }
    else {
      const err = await r.json().catch(() => ({}));
      alert(err.error || 'Failed to save staff member');
    }
  };

  const deactivate = async (id) => {
    if (!confirm('Deactivate this staff member?')) return;
    await authFetch(`${API}/staff/${id}`, { method: 'DELETE' });
    load();
  };

  const sf = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const STATUS_DOT = { in: '#00C9B1', out: '#3D4559', absent: '#FF6B6B', done: '#3D4559' };
  const STATUS_BADGE = {
    in:     { bg: 'rgba(0,201,177,0.12)',   color: '#00C9B1',  text: '✓ Clocked In' },
    out:    { bg: 'rgba(255,255,255,0.06)', color: '#6B7A99',  text: 'Not Yet In' },
    absent: { bg: 'rgba(255,107,107,0.12)', color: '#FF6B6B',  text: 'Absent' },
    done:   { bg: 'rgba(255,255,255,0.06)', color: '#6B7A99',  text: 'Done' },
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button className="sm-btn-gold" onClick={openNew}>+ Add Staff Member</button>
      </div>
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
          {Array(5).fill(0).map((_, i) => <div key={i} className="sm-card"><Sk h={160} r={12} /></div>)}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
          {staff.map((s) => {
            const st = getStatus(s.id);
            const badge = STATUS_BADGE[st];
            return (
              <div key={s.id} className={`sm-staff-card${st === 'in' ? ' clocked-in' : ''}`} onClick={() => openEdit(s)}>
                <div style={{ position: 'absolute', top: 14, right: 14, width: 10, height: 10, borderRadius: '50%', background: STATUS_DOT[st], boxShadow: st === 'in' ? '0 0 8px rgba(0,201,177,0.6)' : 'none' }} />
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: avatarGrad(s.id), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color: '#0B0E17', marginBottom: 12 }}>
                  {initials(s)}
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 3, letterSpacing: -0.3, color: '#FFFFFF' }}>{s.firstName} {s.lastName}</div>
                <div style={{ fontSize: 11, color: '#6B7A99', fontWeight: 600, marginBottom: 10 }}>{s.role}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {[['Base', GHS(s.basicSalary)], ['Comm.', `${(Number(s.commissionRate) * 100).toFixed(0)}%`]].map(([lbl, val]) => (
                    <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6B7A99' }}>
                      <span>{lbl}:</span>
                      <span style={{ fontFamily: '"JetBrains Mono",monospace', fontWeight: 700, color: '#FFFFFF', fontSize: 11 }}>{val}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 100, marginTop: 10, background: badge.bg, color: badge.color }}>
                  {badge.text}
                </div>
                {s.isActive && (
                  <button onClick={(e) => { e.stopPropagation(); deactivate(s.id); }} style={{ position: 'absolute', bottom: 12, right: 12, padding: '3px 8px', borderRadius: 6, background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.2)', color: '#FF6B6B', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="sm-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="sm-modal" style={{ maxWidth: 580 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#FFFFFF' }}>{editing ? 'Edit Staff' : 'Add Staff Member'}</div>
              <ModalClose onClose={() => setShowForm(false)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[['firstName','First Name'],['lastName','Last Name'],['phone','Phone'],['email','Email']].map(([k, l]) => (
                <div key={k}><label className="sm-label">{l}</label><input className="sm-input" value={form[k]} onChange={sf(k)} /></div>
              ))}
              <div>
                <label className="sm-label">Role</label>
                <select className="sm-input" value={form.role} onChange={sf('role')}>
                  {['cashier','staff','supervisor','manager','owner'].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div><label className="sm-label">Basic Salary (GHS)</label><input className="sm-input" type="number" value={form.basicSalary} onChange={sf('basicSalary')} /></div>
              <div><label className="sm-label">Commission (%)</label><input className="sm-input" type="number" step="0.1" value={form.commissionRate} onChange={sf('commissionRate')} /></div>
              <div><label className="sm-label">Transport Allow (GHS)</label><input className="sm-input" type="number" value={form.transportAllow} onChange={sf('transportAllow')} /></div>
              <div><label className="sm-label">Meal Allow (GHS)</label><input className="sm-input" type="number" value={form.mealAllow} onChange={sf('mealAllow')} /></div>
              <div><label className="sm-label">SSNIT Number</label><input className="sm-input" value={form.ssnitNumber} onChange={sf('ssnitNumber')} /></div>
              <div><label className="sm-label">Ghana Card Number</label><input className="sm-input" value={form.ghanaCardNumber} onChange={sf('ghanaCardNumber')} /></div>
              <div><label className="sm-label">Bank Name</label><input className="sm-input" value={form.bankName} onChange={sf('bankName')} /></div>
              <div><label className="sm-label">Bank Account No.</label><input className="sm-input" value={form.bankAccount} onChange={sf('bankAccount')} /></div>
              <div><label className="sm-label">Bank Code (GHipss)</label><input className="sm-input" placeholder="e.g. 030100" value={form.bankCode} onChange={sf('bankCode')} /></div>
              <div>
                <label className="sm-label">MoMo Network</label>
                <select className="sm-input" value={form.momoNetwork} onChange={sf('momoNetwork')}>
                  {['MTN', 'Vodafone', 'AirtelTigo'].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div><label className="sm-label">MoMo Number</label><input className="sm-input" placeholder="024XXXXXXX" value={form.momoNumber} onChange={sf('momoNumber')} /></div>
            </div>
            <div style={{ marginTop: 14 }}>
              <label className="sm-label">Notes</label>
              <textarea className="sm-input" rows={2} value={form.notes} onChange={sf('notes')} style={{ resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button className="sm-btn-gold" style={{ flex: 1, justifyContent: 'center' }} onClick={save}>{editing ? 'Save Changes' : 'Add Staff'}</button>
              <button onClick={() => setShowForm(false)} style={{ flex: 1, padding: '10px 0', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, cursor: 'pointer', fontWeight: 700, color: '#FFFFFF', fontFamily: 'inherit', fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shifts Tab ────────────────────────────────────────────────────────────────
const SHIFT_TYPES = ['morning', 'afternoon', 'evening', 'off', 'holiday'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function ShiftsTab() {
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const weekDates = getWeekDates(weekOffset);

  const load = useCallback(async () => {
    const weekStr = weekDates[0].toISOString().split('T')[0];
    const [sR, shR] = await Promise.all([
      authFetch(`${API}/staff`),
      authFetch(`${API}/staff/shifts?week=${weekStr}`),
    ]);
    if (sR.ok) setStaff(await sR.json());
    if (shR.ok) {
      const data = await shR.json();
      setShifts(Array.isArray(data) ? data : (data.shifts || []));
      if (!Array.isArray(data) && Array.isArray(data.staff)) setStaff(data.staff);
    }
  }, [weekOffset]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const getShift = (staffId, date) => {
    const d = date.toISOString().split('T')[0];
    return shifts.find(s => s.staffId === staffId && s.date.startsWith(d));
  };

  const setShiftType = async (staffId, date, shiftType) => {
    await authFetch(`${API}/staff/shifts`, {
      method: 'POST',
      body: JSON.stringify({ staffId, date: date.toISOString().split('T')[0], shiftType }),
    });
    load();
  };

  const today = new Date().toDateString();

  return (
    <div className="sm-card">
      {/* Week nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button className="sm-btn-ghost" onClick={() => setWeekOffset(w => w - 1)}>←</button>
        <span style={{ fontWeight: 700, fontSize: 13, color: '#FFFFFF', flex: 1, textAlign: 'center' }}>
          {weekDates[0].toLocaleDateString('en-GH', { day: 'numeric', month: 'short' })} – {weekDates[6].toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
        <button className="sm-btn-ghost" onClick={() => setWeekOffset(w => w + 1)}>→</button>
        <button className="sm-btn-gold" onClick={() => setWeekOffset(0)}>Today</button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '130px repeat(7,1fr)', gap: 4, minWidth: 700 }}>
          {/* Header */}
          <div />
          {weekDates.map((d, i) => {
            const isToday = d.toDateString() === today;
            return (
              <div key={i} style={{ textAlign: 'center', paddingBottom: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: isToday ? '#F0B429' : '#3D4559', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{DAY_LABELS[i]}</div>
                <div style={{ fontSize: 11, color: isToday ? '#F0B429' : '#6B7A99', fontWeight: 600 }}>{d.getDate()}</div>
              </div>
            );
          })}
          {/* Rows */}
          {staff.map((s) => (
            <React.Fragment key={s.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 40 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: avatarGrad(s.id), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 900, color: '#0B0E17', flexShrink: 0 }}>
                  {initials(s)}
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#6B7A99', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.firstName}</span>
              </div>
              {weekDates.map((d, i) => {
                const shift = getShift(s.id, d);
                const type = shift?.shiftType || 'off';
                const ss = SHIFT_STYLES[type] || SHIFT_STYLES.off;
                return (
                  <div key={i} style={{ height: 40 }}>
                    <select
                      value={type}
                      onChange={e => setShiftType(s.id, d, e.target.value)}
                      className="sm-shift-select"
                      style={{ height: '100%', background: ss.bg, color: ss.color }}
                    >
                      {SHIFT_TYPES.map(t => <option key={t} value={t}>{SHIFT_STYLES[t]?.label || t}</option>)}
                    </select>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
        {Object.entries(SHIFT_STYLES).map(([key, ss]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6B7A99', fontWeight: 600 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: ss.color }} />
            {ss.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Attendance Tab ────────────────────────────────────────────────────────────
const ATT_STATUS = {
  present: { bg: 'rgba(0,201,177,0.12)',   color: '#00C9B1' },
  absent:  { bg: 'rgba(255,107,107,0.12)', color: '#FF6B6B' },
  late:    { bg: 'rgba(240,180,41,0.12)',  color: '#F0B429' },
  halfday: { bg: 'rgba(56,189,248,0.12)',  color: '#38BDF8' },
};

function AttendanceTab() {
  const [attendance, setAttendance] = useState([]);
  const [staff, setStaff] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showClockIn, setShowClockIn] = useState(false);
  const [manualForm, setManualForm] = useState({ staffId: '', status: 'present', clockIn: '', clockOut: '', notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aR, sR] = await Promise.all([
        authFetch(`${API}/staff/attendance?date=${date}`),
        authFetch(`${API}/staff`),
      ]);
      if (aR.ok) {
        const data = await aR.json();
        setAttendance(Array.isArray(data) ? data : (data.records || []));
      }
      if (sR.ok) setStaff(await sR.json());
    } catch (err) { console.error('Attendance load error:', err); }
    setLoading(false);
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const saveManual = async () => {
    try {
      await authFetch(`${API}/staff/attendance`, {
        method: 'POST',
        body: JSON.stringify({ ...manualForm, staffId: parseInt(manualForm.staffId), date }),
      });
    } catch (err) { console.error('Attendance save error:', err); }
    setShowManual(false);
    load();
  };

  const mf = (k) => (e) => setManualForm(f => ({ ...f, [k]: e.target.value }));
  const cols = '2fr 1fr 1fr 1fr 1fr 1fr';

  return (
    <div className="sm-card">
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <input type="date" className="sm-input" value={date} onChange={e => setDate(e.target.value)} style={{ width: 'auto' }} />
        <button className="sm-btn-gold" onClick={() => setShowClockIn(true)}>🕐 Clock In / Out</button>
        <button className="sm-btn-ghost" onClick={() => setShowManual(true)}>+ Mark Attendance</button>
      </div>

      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: cols, padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, marginBottom: 4 }}>
        {['Staff', 'Status', 'Clock In', 'Clock Out', 'Hours', 'Notes'].map(h => (
          <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{h}</div>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {Array(4).fill(0).map((_, i) => <Sk key={i} h={48} r={10} />)}
        </div>
      ) : attendance.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#6B7A99', fontSize: 13 }}>No attendance records for this date</div>
      ) : attendance.map((a) => {
        const ss = ATT_STATUS[a.status] || { bg: 'rgba(255,255,255,0.06)', color: '#6B7A99' };
        const hrs = a.clockIn && a.clockOut
          ? `${((new Date(a.clockOut) - new Date(a.clockIn)) / 3_600_000).toFixed(1)}h`
          : a.hoursWorked ? `${Number(a.hoursWorked).toFixed(1)}h` : '—';
        return (
          <div key={a.id} className="sm-table-row" style={{ gridTemplateColumns: cols }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: avatarGrad(a.staffId), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900, color: '#0B0E17', flexShrink: 0 }}>
                {a.staff ? initials(a.staff) : '?'}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF' }}>{a.staff?.firstName} {a.staff?.lastName}</div>
                <div style={{ fontSize: 10, color: '#6B7A99' }}>{a.staff?.role}</div>
              </div>
            </div>
            <div><span style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 100, background: ss.bg, color: ss.color }}>{a.status}</span></div>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: '"JetBrains Mono",monospace', color: '#6B7A99' }}>
              {a.clockIn ? new Date(a.clockIn).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' }) : '—'}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: '"JetBrains Mono",monospace', color: '#6B7A99' }}>
              {a.clockOut ? new Date(a.clockOut).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' }) : '—'}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: '"JetBrains Mono",monospace', color: '#00C9B1' }}>{hrs}</div>
            <div style={{ fontSize: 11, color: '#6B7A99' }}>{a.notes || '—'}</div>
          </div>
        );
      })}

      {showClockIn && (
        <ClockInModal staff={staff} onClose={() => setShowClockIn(false)} onSuccess={load} />
      )}

      {showManual && (
        <div className="sm-overlay" onClick={e => e.target === e.currentTarget && setShowManual(false)}>
          <div className="sm-modal" style={{ maxWidth: 400 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#FFFFFF' }}>Mark Attendance — {date}</div>
              <ModalClose onClose={() => setShowManual(false)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label className="sm-label">Staff</label>
                <select className="sm-input" value={manualForm.staffId} onChange={mf('staffId')}>
                  <option value="">Select staff…</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                </select>
              </div>
              <div>
                <label className="sm-label">Status</label>
                <select className="sm-input" value={manualForm.status} onChange={mf('status')}>
                  {['present','absent','late','halfday'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label className="sm-label">Clock In</label><input type="time" className="sm-input" value={manualForm.clockIn} onChange={mf('clockIn')} /></div>
                <div><label className="sm-label">Clock Out</label><input type="time" className="sm-input" value={manualForm.clockOut} onChange={mf('clockOut')} /></div>
              </div>
              <div><label className="sm-label">Notes</label><input className="sm-input" value={manualForm.notes} onChange={mf('notes')} /></div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button className="sm-btn-gold" style={{ flex: 1, justifyContent: 'center' }} onClick={saveManual}>Save</button>
              <button onClick={() => setShowManual(false)} style={{ flex: 1, padding: '10px 0', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, cursor: 'pointer', fontWeight: 700, color: '#FFFFFF', fontFamily: 'inherit', fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Commissions Tab ───────────────────────────────────────────────────────────
function CommissionsTab() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${API}/staff/commissions?month=${month}&year=${year}`);
    if (r.ok) setData(await r.json());
    setLoading(false);
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const totalSales = data.reduce((s, d) => s + Number(d.totalSalesValue ?? d.totalSales ?? 0), 0);
  const totalComm = data.reduce((s, d) => s + Number(d.commissionEarned || 0), 0);
  const maxSales = Math.max(...data.map(d => Number(d.totalSalesValue ?? d.totalSales ?? 0)), 1);

  return (
    <div>
      {/* Period picker */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <select className="sm-input" value={month} onChange={e => setMonth(Number(e.target.value))} style={{ width: 'auto' }}>
          {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select className="sm-input" value={year} onChange={e => setYear(Number(e.target.value))} style={{ width: 'auto' }}>
          {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Summary strip */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[['Total Staff Sales', GHS(totalSales), '#F0B429'], ['Total Commissions', GHS(totalComm), '#00C9B1']].map(([lbl, val, color]) => (
          <div key={lbl} className="sm-card">
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>{lbl}</div>
            <div style={{ fontSize: 22, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color }}>{val}</div>
          </div>
        ))}
      </div>

      <div className="sm-card">
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Array(4).fill(0).map((_, i) => <Sk key={i} h={44} r={10} />)}
          </div>
        ) : data.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#6B7A99', fontSize: 13 }}>No commission data for this period</div>
        ) : data.map((d) => {
          const sales = Number(d.totalSalesValue ?? d.totalSales ?? 0);
          const name = d.name || `${d.firstName || ''} ${d.lastName || ''}`.trim();
          return (
            <div key={d.staffId} className="sm-comm-row">
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: avatarGrad(d.staffId), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#0B0E17', flexShrink: 0 }}>
                {(name.split(' ').map(w => w[0]).join('') || '?').toUpperCase().slice(0, 2)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF' }}>{name}</div>
                <div style={{ fontSize: 11, color: '#6B7A99' }}>{GHS(sales)} in sales · {(Number(d.commissionRate) * 100).toFixed(1)}% rate</div>
              </div>
              <div style={{ width: 70, height: 4, borderRadius: 100, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }}>
                <div style={{ height: '100%', width: `${Math.min(100, (sales / maxSales) * 100)}%`, borderRadius: 100, background: '#F0B429' }} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#F0B429', flexShrink: 0, minWidth: 70, textAlign: 'right' }}>{GHS(d.commissionEarned)}</div>
            </div>
          );
        })}
        {data.length > 0 && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(240,180,41,0.06)', border: '1px solid rgba(240,180,41,0.15)', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#F0B429' }}>Total Commissions</div>
            <div style={{ fontSize: 16, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#F0B429' }}>{GHS(totalComm)}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Payroll Tab ───────────────────────────────────────────────────────────────
const PAY_STATUS = {
  draft:    { bg: 'rgba(255,255,255,0.06)', color: '#6B7A99' },
  approved: { bg: 'rgba(56,189,248,0.12)',  color: '#38BDF8' },
  paid:     { bg: 'rgba(0,201,177,0.12)',   color: '#00C9B1' },
};

function PayrollTab() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [staff, setStaff] = useState([]);
  const [showAdvance, setShowAdvance] = useState(false);
  const [advForm, setAdvForm] = useState({ staffId: '', amount: '', reason: '', deductMonth: now.getMonth() + 1, deductYear: now.getFullYear() });
  const [payModal, setPayModal] = useState(null); // { payslip, staffRecord }
  const [disbursing, setDisbursing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [pR, sR] = await Promise.all([
      authFetch(`${API}/staff/payroll?month=${month}&year=${year}`),
      authFetch(`${API}/staff`),
    ]);
    if (pR.ok) setPayslips(await pR.json());
    if (sR.ok) setStaff(await sR.json());
    setLoading(false);
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    await authFetch(`${API}/staff/payroll/generate`, { method: 'POST', body: JSON.stringify({ month, year }) });
    load();
    setGenerating(false);
  };

  const approve = async (staffId) => {
    await authFetch(`${API}/staff/payroll/${staffId}/approve`, { method: 'POST', body: JSON.stringify({ month, year }) });
    load();
  };

  const markPaid = async (staffId) => {
    await authFetch(`${API}/staff/payroll/${staffId}/mark-paid`, { method: 'POST', body: JSON.stringify({ month, year }) });
    load();
  };

  const openPayModal = (p) => {
    const staffRecord = staff.find(s => s.id === p.staffId);
    setPayModal({ payslip: p, staffRecord });
  };

  const disburse = async (method) => {
    if (!payModal) return;
    setDisbursing(true);
    const r = await authFetch(`${API}/staff/payroll/${payModal.payslip.staffId}/disburse`, {
      method: 'POST',
      body: JSON.stringify({ month, year, method }),
    });
    setDisbursing(false);
    setPayModal(null);
    if (r.ok) load();
    else {
      const err = await r.json().catch(() => ({}));
      alert(err.error || 'Disbursement failed');
    }
  };

  const downloadPdf = (staffId) => {
    window.open(`${API}/staff/payroll/${staffId}/payslip-pdf?month=${month}&year=${year}&token=${tok()}`, '_blank');
  };

  const saveAdvance = async () => {
    await authFetch(`${API}/staff/advances`, { method: 'POST', body: JSON.stringify(advForm) });
    setShowAdvance(false);
    load();
  };

  const totalGross = payslips.reduce((s, p) => s + Number(p.grossPay || 0), 0);
  const totalNet = payslips.reduce((s, p) => s + Number(p.netPay || 0), 0);

  const af = (k) => (e) => setAdvForm(f => ({ ...f, [k]: e.target.value }));
  const cols = '2fr 1fr 1fr 1fr 1fr 1fr 1fr 120px';

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <select className="sm-input" value={month} onChange={e => setMonth(Number(e.target.value))} style={{ width: 'auto' }}>
          {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select className="sm-input" value={year} onChange={e => setYear(Number(e.target.value))} style={{ width: 'auto' }}>
          {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button className="sm-btn-gold" onClick={generate} disabled={generating}>
          {generating ? 'Generating…' : 'Generate Payroll'}
        </button>
        <button className="sm-btn-ghost" onClick={() => setShowAdvance(true)}>+ Salary Advance</button>
      </div>

      {/* KPI strip */}
      {payslips.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 12, marginBottom: 16 }}>
          {[
            ['Total Gross', GHS(totalGross), '#F0B429'],
            ['Total Net Pay', GHS(totalNet), '#00C9B1'],
            ['Staff Count', payslips.length, '#FFFFFF'],
            ['Paid', `${payslips.filter(p => p.status === 'paid').length} / ${payslips.length}`, '#38BDF8'],
          ].map(([lbl, val, color]) => (
            <div key={lbl} className="sm-card">
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>{lbl}</div>
              <div style={{ fontSize: 20, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color }}>{val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Ghana Tax Info */}
      <div className="sm-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#FFFFFF' }}>Ghana Payroll Tax — Auto-calculated</div>
          <span className="sm-chip sky">SSNIT · PAYE · Tier 2</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          {[
            { label: 'SSNIT Tier 1', rows: [['Employee', '5.5% of basic'], ['Employer', '13% of basic']], color: '#38BDF8' },
            { label: 'SSNIT Tier 2', rows: [['Employee', '5% of basic']], color: '#00C9B1' },
            { label: 'PAYE Income Tax', rows: [['GHS 0–402', '0%'], ['GHS 402–510', '5%'], ['GHS 510–840', '10%'], ['GHS 840–2,760', '17.5%']], color: '#A78BFA' },
          ].map(({ label, rows, color }) => (
            <div key={label} style={{ padding: '12px 14px', borderRadius: 12, background: color + '08', border: `1px solid ${color}30` }}>
              <div style={{ fontSize: 12, fontWeight: 800, color, marginBottom: 8 }}>{label}</div>
              {rows.map(([lbl, val]) => (
                <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                  <span style={{ color: '#6B7A99', fontWeight: 600 }}>{lbl}</span>
                  <span style={{ fontFamily: '"JetBrains Mono",monospace', fontWeight: 700, color }}>{val}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Payroll table */}
      <div className="sm-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#FFFFFF' }}>Payroll Summary</div>
            <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>{MONTH_NAMES[month - 1]} {year} · with Ghana deductions</div>
          </div>
        </div>

        {/* Table header */}
        <div style={{ display: 'grid', gridTemplateColumns: cols, padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, marginBottom: 4 }}>
          {['Employee', 'Basic', 'Comm.', 'SSNIT', 'Tier 2', 'PAYE', 'Net Pay', 'Actions'].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            {Array(4).fill(0).map((_, i) => <Sk key={i} h={52} r={10} />)}
          </div>
        ) : payslips.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#6B7A99', fontSize: 13 }}>
            No payroll generated. Click "Generate Payroll" to calculate payslips.
          </div>
        ) : (
          <>
            {payslips.map((p) => {
              const ss = PAY_STATUS[p.status] || PAY_STATUS.draft;
              return (
                <div key={p.id} className="sm-table-row" style={{ gridTemplateColumns: cols }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: avatarGrad(p.staffId), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900, color: '#0B0E17', flexShrink: 0 }}>
                      {p.staff ? initials(p.staff) : '?'}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF' }}>{p.staff?.firstName} {p.staff?.lastName}</div>
                      <div style={{ fontSize: 10, color: '#6B7A99' }}>{p.staff?.role}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, fontFamily: '"JetBrains Mono",monospace', color: '#FFFFFF' }}>{Number(p.basicSalary || 0).toFixed(0)}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, fontFamily: '"JetBrains Mono",monospace', color: '#F0B429' }}>{Number(p.commission || 0).toFixed(0)}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B' }}>-{Number(p.ssnitEmployee || 0).toFixed(0)}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B' }}>-{Number(p.ssnitTier2 || 0).toFixed(0)}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B' }}>-{Number(p.incomeTax || 0).toFixed(0)}</div>
                  <div style={{ fontSize: 13, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#00C9B1' }}>{Number(p.netPay || 0).toFixed(0)}</div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {p.status === 'draft' && (
                      <button onClick={() => approve(p.staffId)} className="sm-btn-sm" style={{ background: 'rgba(56,189,248,0.12)', color: '#38BDF8' }}>Approve</button>
                    )}
                    {p.status === 'approved' && !p.transferRef && (
                      <button onClick={() => openPayModal(p)} className="sm-btn-sm" style={{ background: 'rgba(0,201,177,0.12)', color: '#00C9B1' }}>Pay</button>
                    )}
                    {p.transferStatus === 'pending' && (
                      <span className="sm-btn-sm" style={{ background: 'rgba(240,180,41,0.1)', color: '#F0B429', cursor: 'default' }}>Pending…</span>
                    )}
                    {p.transferStatus === 'failed' && (
                      <button onClick={() => openPayModal(p)} className="sm-btn-sm" style={{ background: 'rgba(255,107,107,0.12)', color: '#FF6B6B' }}>Retry</button>
                    )}
                    <button onClick={() => downloadPdf(p.staffId)} className="sm-btn-sm" style={{ background: 'rgba(240,180,41,0.1)', border: '1px solid rgba(240,180,41,0.25)', color: '#F0B429' }}>PDF</button>
                  </div>
                </div>
              );
            })}
            {/* Totals row */}
            <div style={{ display: 'grid', gridTemplateColumns: cols, padding: '12px', background: 'rgba(240,180,41,0.05)', borderRadius: 10, marginTop: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#F0B429' }}>Totals</div>
              <div style={{ fontSize: 13, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#F0B429' }}>{payslips.reduce((s, p) => s + Number(p.basicSalary || 0), 0).toFixed(0)}</div>
              <div style={{ fontSize: 13, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#F0B429' }}>{payslips.reduce((s, p) => s + Number(p.commission || 0), 0).toFixed(0)}</div>
              <div style={{ fontSize: 13, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B' }}>-{payslips.reduce((s, p) => s + Number(p.ssnitEmployee || 0), 0).toFixed(0)}</div>
              <div style={{ fontSize: 13, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B' }}>-{payslips.reduce((s, p) => s + Number(p.ssnitTier2 || 0), 0).toFixed(0)}</div>
              <div style={{ fontSize: 13, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B' }}>-{payslips.reduce((s, p) => s + Number(p.incomeTax || 0), 0).toFixed(0)}</div>
              <div style={{ fontSize: 14, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#F0B429' }}>{totalNet.toFixed(0)}</div>
              <div />
            </div>
          </>
        )}
      </div>

      {/* Pay / Disburse modal */}
      {payModal && (
        <div className="sm-overlay" onClick={e => e.target === e.currentTarget && setPayModal(null)}>
          <div className="sm-modal" style={{ maxWidth: 420 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#FFFFFF' }}>Pay Staff Member</div>
              <ModalClose onClose={() => setPayModal(null)} />
            </div>

            {/* Summary */}
            <div style={{ padding: '14px 16px', background: 'rgba(0,201,177,0.08)', border: '1px solid rgba(0,201,177,0.2)', borderRadius: 12, marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: '#6B7A99', marginBottom: 4 }}>
                {payModal.payslip?.staff?.firstName || payModal.staffRecord?.firstName} {payModal.payslip?.staff?.lastName || payModal.staffRecord?.lastName}
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#00C9B1' }}>
                GHS {Number(payModal.payslip.netPay).toFixed(2)}
              </div>
              <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>Net Pay · {MONTH_NAMES[month - 1]} {year}</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {/* MoMo option */}
              {payModal.staffRecord?.momoNumber ? (
                <button
                  onClick={() => disburse('momo')}
                  disabled={disbursing}
                  style={{ padding: '14px 16px', border: '2px solid rgba(0,201,177,0.4)', borderRadius: 12, background: 'rgba(0,201,177,0.06)', cursor: 'pointer', textAlign: 'left', opacity: disbursing ? 0.6 : 1 }}
                >
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#00C9B1', marginBottom: 2 }}>Mobile Money</div>
                  <div style={{ fontSize: 11, color: '#6B7A99' }}>{payModal.staffRecord.momoNetwork} · {payModal.staffRecord.momoNumber}</div>
                </button>
              ) : (
                <div style={{ padding: '12px 16px', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 12, color: '#6B7A99', fontSize: 12 }}>
                  No MoMo number on file — add it in the staff profile to enable mobile money.
                </div>
              )}

              {/* Bank option */}
              {payModal.staffRecord?.bankAccount && payModal.staffRecord?.bankCode ? (
                <button
                  onClick={() => disburse('bank')}
                  disabled={disbursing}
                  style={{ padding: '14px 16px', border: '2px solid rgba(56,189,248,0.3)', borderRadius: 12, background: 'rgba(56,189,248,0.06)', cursor: 'pointer', textAlign: 'left', opacity: disbursing ? 0.6 : 1 }}
                >
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#38BDF8', marginBottom: 2 }}>Bank Transfer</div>
                  <div style={{ fontSize: 11, color: '#6B7A99' }}>{payModal.staffRecord.bankName} · {payModal.staffRecord.bankAccount}</div>
                </button>
              ) : (
                <div style={{ padding: '12px 16px', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 12, color: '#6B7A99', fontSize: 12 }}>
                  No bank details on file — add bank account + bank code in the staff profile.
                </div>
              )}

              {/* Manual option */}
              <button
                onClick={() => disburse('manual')}
                disabled={disbursing}
                style={{ padding: '14px 16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, background: 'transparent', cursor: 'pointer', textAlign: 'left', opacity: disbursing ? 0.6 : 1 }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', marginBottom: 2 }}>Mark as Manually Paid</div>
                <div style={{ fontSize: 11, color: '#6B7A99' }}>Cash, cheque, or external transfer — just record it as paid</div>
              </button>
            </div>

            <button onClick={() => setPayModal(null)} style={{ width: '100%', padding: '10px 0', background: 'transparent', border: 'none', cursor: 'pointer', color: '#6B7A99', fontSize: 13, fontFamily: 'inherit' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Advance modal */}
      {showAdvance && (
        <div className="sm-overlay" onClick={e => e.target === e.currentTarget && setShowAdvance(false)}>
          <div className="sm-modal" style={{ maxWidth: 400 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#FFFFFF' }}>Record Salary Advance</div>
              <ModalClose onClose={() => setShowAdvance(false)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label className="sm-label">Staff</label>
                <select className="sm-input" value={advForm.staffId} onChange={af('staffId')}>
                  <option value="">Select staff…</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                </select>
              </div>
              <div><label className="sm-label">Amount (GHS)</label><input type="number" className="sm-input" value={advForm.amount} onChange={af('amount')} /></div>
              <div><label className="sm-label">Reason</label><input className="sm-input" value={advForm.reason} onChange={af('reason')} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label className="sm-label">Deduct Month</label>
                  <select className="sm-input" value={advForm.deductMonth} onChange={e => setAdvForm(f => ({ ...f, deductMonth: Number(e.target.value) }))}>
                    {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="sm-label">Deduct Year</label>
                  <select className="sm-input" value={advForm.deductYear} onChange={e => setAdvForm(f => ({ ...f, deductYear: Number(e.target.value) }))}>
                    {[2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button className="sm-btn-gold" style={{ flex: 1, justifyContent: 'center' }} onClick={saveAdvance}>Save Advance</button>
              <button onClick={() => setShowAdvance(false)} style={{ flex: 1, padding: '10px 0', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, cursor: 'pointer', fontWeight: 700, color: '#FFFFFF', fontFamily: 'inherit', fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Advances Tab ──────────────────────────────────────────────────────────────
function AdvancesTab() {
  const now = new Date();
  const [advances, setAdvances] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [advForm, setAdvForm] = useState({ staffId: '', amount: '', reason: '', deductMonth: now.getMonth() + 1, deductYear: now.getFullYear() });

  const load = useCallback(async () => {
    setLoading(true);
    const [aR, sR] = await Promise.all([
      authFetch(`${API}/staff/advances`),
      authFetch(`${API}/staff`),
    ]);
    if (aR.ok) setAdvances(await aR.json());
    if (sR.ok) setStaff(await sR.json());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    await authFetch(`${API}/staff/advances`, { method: 'POST', body: JSON.stringify(advForm) });
    setShowForm(false);
    load();
  };

  const af = (k) => (e) => setAdvForm(f => ({ ...f, [k]: e.target.value }));
  const pending = advances.filter(a => a.status === 'pending');
  const total = pending.reduce((s, a) => s + Number(a.amount || 0), 0);

  const ADV_STATUS = {
    pending:  { bg: 'rgba(240,180,41,0.12)',  color: '#F0B429',  label: 'Pending' },
    paid:     { bg: 'rgba(0,201,177,0.12)',   color: '#00C9B1',  label: 'Paid' },
    overdue:  { bg: 'rgba(255,107,107,0.12)', color: '#FF6B6B',  label: 'Overdue' },
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div className="sm-card" style={{ flex: 1, marginRight: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Outstanding Advances</div>
          <div style={{ fontSize: 22, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B' }}>{GHS(total)}</div>
          <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 4 }}>{pending.length} outstanding</div>
        </div>
        <button className="sm-btn-gold" onClick={() => setShowForm(true)}>+ Issue Advance</button>
      </div>

      <div className="sm-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#FFFFFF' }}>Salary Advances</div>
          <span className="sm-chip coral">{GHS(total)} owed</span>
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Array(3).fill(0).map((_, i) => <Sk key={i} h={64} r={12} />)}
          </div>
        ) : advances.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#6B7A99', fontSize: 13 }}>No advances recorded</div>
        ) : advances.map((a) => {
          const ss = ADV_STATUS[a.status] || ADV_STATUS.pending;
          return (
            <div key={a.id} className="sm-adv-item">
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: a.staff ? avatarGrad(a.staffId) : 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, color: '#0B0E17', flexShrink: 0 }}>
                {a.staff ? initials(a.staff) : '?'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF' }}>{a.staff?.firstName} {a.staff?.lastName}</div>
                <div style={{ fontSize: 10, color: '#6B7A99', marginTop: 1 }}>
                  Deduct: {MONTH_NAMES[(a.deductMonth || 1) - 1]} {a.deductYear}
                  {a.reason ? ` · ${a.reason}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B' }}>{GHS(a.amount)}</div>
                <div style={{ display: 'inline-flex', fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 100, marginTop: 4, background: ss.bg, color: ss.color }}>
                  {ss.label}
                </div>
              </div>
            </div>
          );
        })}

        <button
          onClick={() => setShowForm(true)}
          style={{ width: '100%', padding: '11px', borderRadius: 12, marginTop: 8, background: 'rgba(255,107,107,0.05)', border: '1px dashed rgba(255,107,107,0.2)', color: '#FF6B6B', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
          onMouseOver={e => e.currentTarget.style.background = 'rgba(255,107,107,0.1)'}
          onMouseOut={e => e.currentTarget.style.background = 'rgba(255,107,107,0.05)'}
        >
          + Issue New Advance
        </button>
      </div>

      {showForm && (
        <div className="sm-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="sm-modal" style={{ maxWidth: 400 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#FFFFFF' }}>Issue Salary Advance</div>
              <ModalClose onClose={() => setShowForm(false)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label className="sm-label">Staff</label>
                <select className="sm-input" value={advForm.staffId} onChange={af('staffId')}>
                  <option value="">Select staff…</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                </select>
              </div>
              <div><label className="sm-label">Amount (GHS)</label><input type="number" className="sm-input" value={advForm.amount} onChange={af('amount')} /></div>
              <div><label className="sm-label">Reason</label><input className="sm-input" value={advForm.reason} onChange={af('reason')} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label className="sm-label">Deduct Month</label>
                  <select className="sm-input" value={advForm.deductMonth} onChange={e => setAdvForm(f => ({ ...f, deductMonth: Number(e.target.value) }))}>
                    {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="sm-label">Deduct Year</label>
                  <select className="sm-input" value={advForm.deductYear} onChange={e => setAdvForm(f => ({ ...f, deductYear: Number(e.target.value) }))}>
                    {[2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button className="sm-btn-gold" style={{ flex: 1, justifyContent: 'center' }} onClick={save}>Save Advance</button>
              <button onClick={() => setShowForm(false)} style={{ flex: 1, padding: '10px 0', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, cursor: 'pointer', fontWeight: 700, color: '#FFFFFF', fontFamily: 'inherit', fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function StaffManagement() {
  return (
    <ErrorBoundary>
      <StaffManagementContent />
    </ErrorBoundary>
  );
}

function StaffManagementContent() {
  const now = new Date();
  const [activeTab, setActiveTab] = useState('Overview');
  const [addStaffOpen, setAddStaffOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState([]);
  const [advanceSummary, setAdvanceSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [showClockIn, setShowClockIn] = useState(false);

  useEffect(() => {
    Promise.all([
      authFetch(`${API}/staff`).then(r => r.ok ? r.json() : []),
      authFetch(`${API}/staff/payroll?month=${now.getMonth() + 1}&year=${now.getFullYear()}`).then(r => r.ok ? r.json() : []),
      authFetch(`${API}/staff/advances`).then(r => r.ok ? r.json() : []),
    ]).then(([staff, payroll, advances]) => {
      setStaffList(staff);
      setPayrollSummary(Array.isArray(payroll) ? payroll : []);
      setAdvanceSummary(Array.isArray(advances) ? advances : []);
      setSummaryLoading(false);
    }).catch(() => setSummaryLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPayroll = staffList.reduce((s, st) => s + Number(st.basicSalary || 0), 0);
  const totalSsnit = payrollSummary.reduce((s, p) => s + Number(p.ssnitEmployee || 0) + Number(p.ssnitEmployer || 0), 0);
  const totalPaye = payrollSummary.reduce((s, p) => s + Number(p.incomeTax || 0), 0);
  const totalAdvances = advanceSummary.filter(a => a.status === 'pending').reduce((s, a) => s + Number(a.amount || 0), 0);
  const activeStaff = staffList.filter(s => s.isActive).length;

  const KPI_CARDS = [
    { label: 'Total Staff',        val: staffList.length,   sub: `${activeStaff} active`, color: 'teal', valColor: '#00C9B1' },
    { label: 'Monthly Payroll',    val: GHS(totalPayroll),  sub: `${MONTH_NAMES[now.getMonth()]} ${now.getFullYear()}`, color: 'gold', valColor: '#F0B429' },
    { label: 'SSNIT Contribution', val: GHS(totalSsnit),   sub: 'Emp + Employer',  color: 'sky',  valColor: '#38BDF8' },
    { label: 'PAYE Tax',           val: GHS(totalPaye),    sub: 'Withheld',        color: 'lav',  valColor: '#A78BFA' },
    { label: 'Salary Advances',    val: GHS(totalAdvances), sub: `${advanceSummary.filter(a => a.status === 'pending').length} outstanding`, color: 'coral', valColor: '#FF6B6B' },
  ];

  return (
    <Layout>
      <style>{STYLES}</style>
      <div className="sm-wrap">

        {/* Topbar */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: -1, lineHeight: 1.1, color: '#FFFFFF' }}>
              Staff &amp; <em style={{ fontFamily: '"Instrument Serif",serif', fontStyle: 'italic', color: '#F0B429' }}>Payroll</em>
            </div>
            <div style={{ fontSize: 13, color: '#6B7A99', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{staffList.length} employees · {MONTH_NAMES[now.getMonth()]} {now.getFullYear()}</span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, background: 'rgba(0,201,177,0.12)', color: '#00C9B1', padding: '3px 9px', borderRadius: 100, border: '1px solid rgba(0,201,177,0.2)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00C9B1', animation: 'sm-blink 1.5s infinite', display: 'inline-block' }} />
                Ghana PAYE Auto-calc
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="sm-btn-ghost" onClick={() => setShowClockIn(true)}>🕐 Clock In / Out</button>
            <button className="sm-btn-ghost" onClick={() => setActiveTab('Advances')}>💸 Salary Advance</button>
            <button className="sm-btn-gold" onClick={() => { setActiveTab('Overview'); setAddStaffOpen(true); }}>+ Add Staff</button>
          </div>
        </div>

        {/* Page tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {TABS.map(tab => (
            <div key={tab} className={`sm-ptab${activeTab === tab ? ' on' : ''}`} onClick={() => setActiveTab(tab)}>
              {tab}
            </div>
          ))}
        </div>

        {/* KPI strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
          {KPI_CARDS.map(({ label, val, sub, color, valColor }) => (
            <div key={label} className={`sm-kpi ${color}`}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>{label}</div>
              {summaryLoading
                ? <Sk h={22} w="80%" r={5} />
                : <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.5, fontFamily: '"JetBrains Mono",monospace', lineHeight: 1, color: valColor }}>{val}</div>
              }
              <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 6, fontWeight: 600 }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'Overview'     && <DirectoryTab forceOpen={addStaffOpen} onForceOpenConsumed={() => setAddStaffOpen(false)} />}
        {activeTab === 'Attendance'   && <AttendanceTab />}
        {activeTab === 'Shifts'       && <ShiftsTab />}
        {activeTab === 'Payroll'      && <PayrollTab />}
        {activeTab === 'Commissions'  && <CommissionsTab />}
        {activeTab === 'Advances'     && <AdvancesTab />}
        {activeTab === 'Audit'        && <AuditTab />}

        {showClockIn && (
          <ClockInModal staff={staffList} onClose={() => setShowClockIn(false)} onSuccess={() => {}} />
        )}

      </div>
    </Layout>
  );
}
